import React, { useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Popover,
  Typography,
  Button,
} from "@mui/material";
import { ChromePicker } from "react-color";
import Palette from "../../Plan/Palette";
import CustomEditActionButtons from "./EditActionButtons";

function EditRowDialog({ table, row, initialData, handleUpdateOrder }) {
  const [newRow, setNewRow] = useState({
    item_name: initialData.item_name || "",
    item_id: initialData.item_id || "",
    material_grouping: initialData.material_grouping || "",
    car_type: initialData.car_type || "",
    spec: initialData.spec || "",
    sub_code: initialData.sub_code || "",
    color: initialData.gantt_color || "#000000",
    safety_stock: initialData.safety_stock || 0,
  });
  const [activeColor, setActiveColor] = useState(initialData.gantt_color);
  const [tempColor, setTempColor] = useState(initialData.gantt_color); // 임시 색상 저장
  const [anchorEl, setAnchorEl] = useState(null);

  const onTileClick = (color) => {
    handleInputChange("gantt_color", color);
    setActiveColor(color);
    setTempColor(color);
  };

  const setEditingRow = (field, value) => {
    table.setEditingRow({
      ...row,
      [field]: value,
    });
  };

  const handleInputChange = (field, value) => {
    setNewRow((prev) => ({ ...prev, [field]: value }));
    if (field === "gantt_color") {
      setEditingRow(field, value);
      setActiveColor(value);
    } else {
      setEditingRow(field, value);
    }
  };

  const handleOpenChromePicker = (event) => {
    setAnchorEl(event.currentTarget);
    setTempColor(activeColor); // 현재 색상을 임시 저장
  };

  const handleCloseChromePicker = () => {
    setAnchorEl(null);
  };

  // ChromePicker 색상 변경 중 (실시간 미리보기)
  const handleColorChange = (color) => {
    setTempColor(color.hex);
  };

  // 확인 버튼 클릭 시
  const handleColorConfirm = () => {
    handleInputChange("gantt_color", tempColor);
    setActiveColor(tempColor);
    handleCloseChromePicker();
  };

  // 취소 버튼 클릭 시
  const handleColorCancel = () => {
    setTempColor(activeColor); // 원래 색상으로 복원
    handleCloseChromePicker();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <DialogTitle
        variant="h5"
        sx={{ fontSize: "18px !important", color: "#111" }}
      >
        제품정보 수정
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
      >
        <TextField
          label="제품명"
          value={newRow.item_name}
          onChange={(e) => handleInputChange("item_name", e.target.value)}
          fullWidth
        />
        <TextField
          label="Material Grouping"
          value={newRow.material_grouping}
          onChange={(e) =>
            handleInputChange("material_grouping", e.target.value)
          }
          fullWidth
        />
        <TextField
          label="차종"
          value={newRow.car_type}
          onChange={(e) => handleInputChange("car_type", e.target.value)}
          fullWidth
        />
        <TextField
          label="규격"
          value={newRow.spec}
          onChange={(e) => handleInputChange("spec", e.target.value)}
          fullWidth
        />
        <TextField
          label="사내품번"
          value={newRow.sub_code}
          onChange={(e) => handleInputChange("sub_code", e.target.value)}
          fullWidth
        />
        <TextField
          label="안전재고"
          fullWidth
          name="safety_stock"
          type="number"
          value={newRow.safety_stock}
          onChange={(e) => handleInputChange("safety_stock", e.target.value)}
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            색상 선택
          </Typography>
          <Palette
            handleOpenChromePicker={handleOpenChromePicker}
            title="더 많은 색상 선택"
            handleInputChange={handleInputChange}
            colors1={[
              { value: "#96bef0" },
              { value: "#ffb5ea" },
              { value: "#8affad" },
              { value: "#9ccdff" },
              { value: "#ea9cfb", lightLabel: true },
              { value: "#f0be68", lightLabel: true },
              { value: "#64f0f0", lightLabel: true },
              { value: "#efb9b9", lightLabel: true },
              { value: "#6ff080", lightLabel: true },
              { value: "#b7e1f0", lightLabel: true },
            ]}
            colors2={[
              { value: "#ffd179" },
              { value: "#c5ff7b" },
              { value: "#64b9ff" },
              { value: "#d87570" },
              { value: "#aff3b2", lightLabel: true },
              { value: "#a2a6d8", lightLabel: true },
              { value: "#ffbe96", lightLabel: true },
              { value: "#c882e2", lightLabel: true },
              { value: "#c4a9d3", lightLabel: true },
              { value: "#aad2e8", lightLabel: true },
            ]}
            activeColor={activeColor}
            setActiveColor={setActiveColor}
            onTileClick={onTileClick}
          />
        </Box>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={(event, reason) => {
            // backdrop 클릭만 닫기 허용, escapeKeyDown도 허용
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              handleColorCancel();
            }
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          disableRestoreFocus
          slotProps={{
            paper: {
              onClick: (e) => {
                // Popover 내부 클릭 시 이벤트 전파 중지
                e.stopPropagation();
              },
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
            onClick={(e) => {
              // Box 내부 클릭 이벤트 전파 중지
              e.stopPropagation();
            }}
          >
            <ChromePicker
              color={tempColor}
              onChange={handleColorChange}
              disableAlpha={false}
            />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleColorCancel}
                sx={{ color: "#fff", borderColor: "#fff" }}
              >
                취소
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleColorConfirm}
                sx={{ bgcolor: "#fff", color: "#000" }}
              >
                확인
              </Button>
            </Box>
          </Box>
        </Popover>
      </DialogContent>
      <DialogActions>
        <CustomEditActionButtons
          table={table}
          row={row}
          handleUpdateOrder={handleUpdateOrder}
          newRow={newRow}
        />
      </DialogActions>
    </>
  );
}

export default EditRowDialog;
